import { useEffect, useState } from "react";
import ModalView from "@modals/modalview/ModalView";
import styles from "./ReportError.module.css";
import closeIcon from "@assets/icons/close-icon.svg";
import {
  REPORT_ERROR_REASON_OPTIONS,
  REPORT_ERROR_REASONS,
  type ReportErrorReason,
} from "@constants/evaluationConstants";
import { toast } from "sonner";

const MAX_COMMENT_LENGTH = 200;

interface ReportErrorProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    pageNumbers: string;
    reason: string;
  }) => Promise<void>;
  isSubmitting?: boolean;
}

function ReportError({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: ReportErrorProps) {
  const [pageNumbers, setPageNumbers] = useState("");
  const [selectedReason, setSelectedReason] = useState<ReportErrorReason | null>(
    null,
  );
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setPageNumbers("");
    setSelectedReason(null);
    setComment("");
  }, [isOpen]);

  const handleReasonChange = (value: ReportErrorReason) => {
    setSelectedReason(value);
    if (value !== REPORT_ERROR_REASONS.OTHER) {
      setComment("");
    }
  };

  const getReasonPayload = (): string | null => {
    if (!selectedReason) return null;

    if (selectedReason === REPORT_ERROR_REASONS.OTHER) {
      const trimmedComment = comment.trim();
      return trimmedComment || null;
    }

    return selectedReason;
  };

  const handleSubmit = async () => {
    const trimmedPageNumbers = pageNumbers.trim();
    const reason = getReasonPayload();

    if (!trimmedPageNumbers) {
      toast.error("Please enter the PDF page number with the issue.");
      return;
    }

    if (!reason) {
      toast.error(
        selectedReason === REPORT_ERROR_REASONS.OTHER
          ? "Please add a comment describing the issue."
          : "Please select an issue.",
      );
      return;
    }

    try {
      await onSubmit({
        pageNumbers: trimmedPageNumbers,
        reason,
      });
      toast.success("Issue reported successfully.");
      onClose();
    } catch {
      // Error toast handled by mutation hook
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    !pageNumbers.trim() ||
    !selectedReason ||
    (selectedReason === REPORT_ERROR_REASONS.OTHER && !comment.trim());

  return (
    <ModalView isOpen={isOpen} onClose={onClose}>
      <div className={styles.reportErrorContainer}>
        <div className={styles.closeHeader}>
          <button className={styles.closeBtn} onClick={onClose}>
            <img src={closeIcon} alt="Close" />
          </button>
        </div>
        <div className={styles.reportErrorContent}>
          <div className={styles.reportErrorTitle}>
            <h2>Report Issue with the Answer Sheet</h2>
          </div>
          <div className={styles.pageNumberSection}>
            <label htmlFor="reportErrorPageNumbers">
              Enter the PDF&apos;s page number with the issue.
            </label>
            <input
              id="reportErrorPageNumbers"
              type="text"
              placeholder="e.g. 1-6, 10, 13"
              className={styles.pageNumberInput}
              value={pageNumbers}
              onChange={(e) => setPageNumbers(e.target.value)}
            />
          </div>
          <div className={styles.descriptionSection}>
            <label>Select the issue</label>
            <ul className={styles.reasonList}>
              {REPORT_ERROR_REASON_OPTIONS.map(({ value, label }) => (
                <li key={value} className={styles.reasonItem}>
                  <input
                    type="radio"
                    name="reportErrorReason"
                    value={value}
                    checked={selectedReason === value}
                    onChange={() => handleReasonChange(value)}
                  />
                  <label>{label}</label>
                </li>
              ))}
            </ul>
          </div>
          {selectedReason === REPORT_ERROR_REASONS.OTHER && (
            <div className={styles.commentSection}>
              <div className={styles.commentHeader}>
                <label htmlFor="reportErrorComment">Add Comment</label>
                <span className={styles.characterLimit}>
                  {MAX_COMMENT_LENGTH} Characters
                </span>
              </div>
              <textarea
                id="reportErrorComment"
                className={styles.commentTextarea}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={MAX_COMMENT_LENGTH}
              />
            </div>
          )}
          <div className={styles.submitButtonSection}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={() => void handleSubmit()}
              disabled={isSubmitDisabled}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </ModalView>
  );
}

export default ReportError;
